import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';

import { PERMISSION_ACTION, PERMISSION_ENTITY } from '@eai-tdv/core/models';

import { AuthGuard } from './core/guards/auth.guard';
import { IsClinicGuard } from './core/guards/is-clinic.guard';
import { NotAuthGuard } from './core/guards/not-auth.guard';
import { PermissionsGuard } from './core/guards/permissions.guard';
import {
  PhoneUnverifiedGuard,
  PhoneVerifiedGuard,
} from './core/guards/phone-verification.guard';
import {
  PaymentFailedGuard,
  SubscriptionDontExistGuard,
  SubscriptionExistGuard,
} from './core/guards/subscriptions.guard';
import {
  AUTHENTICATED_ROUTE_DEFAULT,
  EnvironmentService,
  PATIENT_ROUTE_PARAMS_W,
  ROUTE_NAMES_W,
} from './core/services';
import { createRouteData } from './shared/functions';

const APP_ROUTES: Routes = [
  { path: '', pathMatch: 'full', redirectTo: AUTHENTICATED_ROUTE_DEFAULT }, // todo - should be other base path in future cos PATIENDS can be forbidden

  // NOT SECURED ROUTES
  {
    path: ROUTE_NAMES_W.AUTH,
    canActivate: [NotAuthGuard],
    loadChildren: () =>
      import('./features/auth/auth.module').then(m => m.AuthModule),
  },
  {
    path: ROUTE_NAMES_W.EMAIL_VERIFY,
    loadChildren: () =>
      import('./features/email-verify/email-verify.module').then(
        m => m.EmailChangeModule,
      ),
  },
  {
    path: ROUTE_NAMES_W.REGISTRATION_PASSWORD,
    canActivate: [NotAuthGuard],
    loadChildren: () =>
      import('./features/registration/registration-password.module').then(
        m => m.RegistrationPasswordModule,
      ),
  },
  {
    path: ROUTE_NAMES_W.ADMIN_PIN_RESET,
    loadComponent: () =>
      import('./features/admin-pin-reset/admin-pin-reset.component').then(
        m => m.AdminPinResetComponent,
      ),
  },

  // SECURED ROUTES
  {
    path: ROUTE_NAMES_W.PHONE_VERIFICATION,
    canActivate: [AuthGuard, PhoneUnverifiedGuard],
    loadChildren: () =>
      import('./shared/modules/auth-phone-verification/auth-phone-verification.module').then(
        m => m.AuthPhoneVerificationModule,
      ),
  },
  {
    path: ROUTE_NAMES_W.PROFILE,
    canActivate: [AuthGuard, PhoneVerifiedGuard],
    loadChildren: () =>
      import('./features/profile/profile.module').then(m => m.ProfileModule),
  },
  {
    path: ROUTE_NAMES_W.CHOOSE_PLAN,
    canActivate: [AuthGuard, PhoneVerifiedGuard, SubscriptionDontExistGuard],
    loadChildren: () =>
      import('./features/choose-plan/feature-choose-plan.module').then(
        m => m.ChoosePlanModule,
      ),
  },
  {
    path: ROUTE_NAMES_W.PATIENTS,
    canActivate: [
      AuthGuard,
      PhoneVerifiedGuard,
      SubscriptionExistGuard,
      PaymentFailedGuard,
      PermissionsGuard,
    ],
    data: createRouteData({
      permissions: [
        {
          entity: PERMISSION_ENTITY.PATIENTS,
          action: PERMISSION_ACTION.LIST,
        },
      ],
    }),
    loadComponent: () =>
      import('./features/patient-list/patient-list.component').then(
        m => m.PatientListComponent,
      ),
  },
  {
    // !!!! IMPORTANT - can't be use `${}` here cos NX wrongly handle this type
    path: ROUTE_NAMES_W.PATIENTS + '/:' + PATIENT_ROUTE_PARAMS_W.PATIENT_UUID,
    canActivate: [
      AuthGuard,
      PhoneVerifiedGuard,
      SubscriptionExistGuard,
      PaymentFailedGuard,
      PermissionsGuard,
    ],
    data: createRouteData({
      permissions: [
        {
          entity: PERMISSION_ENTITY.PATIENTS,
          action: PERMISSION_ACTION.VIEW,
        },
      ],
    }),
    loadChildren: () =>
      import('./features/patient/patient-web.module').then(
        m => m.PatientWebModule,
      ),
  },
  {
    path: ROUTE_NAMES_W.PAYMENT_FAILED,
    canActivate: [AuthGuard, PhoneVerifiedGuard, SubscriptionExistGuard],
    loadChildren: () =>
      import('./features/payment-failed/payment-failed.module').then(
        m => m.PaymentFailedModule,
      ),
  },

  {
    path: ROUTE_NAMES_W.SWITCH_PROFILE,
    canActivate: [AuthGuard, IsClinicGuard],
    loadComponent: () =>
      // we use login component cos it has full logic for choose profile with skip step 'username/password' (set PIN, create new PIN, forgot PIN etc )
      import('./features/login/login.component').then(m => m.LoginComponent),
  },

  {
    path: ROUTE_NAMES_W.LOGOUT,
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./features/logout/logout.component').then(m => m.LogoutComponent),
  },
];

if (EnvironmentService.isDev || EnvironmentService.isLocal) {
  APP_ROUTES.push({
    path: ROUTE_NAMES_W.DEVELOPMENT,
    loadComponent: () =>
      import('./features/development/dev.component').then(
        m => m.DevelopmentComponent,
      ),
  });
}

APP_ROUTES.push({ path: '**', redirectTo: AUTHENTICATED_ROUTE_DEFAULT });

@NgModule({
  imports: [
    RouterModule.forRoot(APP_ROUTES, {
      preloadingStrategy: PreloadAllModules,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
